import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, LayoutDashboard, CheckCircle2, XCircle, Hourglass, ThumbsUp,
  MessageCircle, ExternalLink, Timer, TrendingUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

const ROLE_LABEL = {
  sales_executive:        "Sales Executive",
  sales_manager:          "Sales Manager",
  support_executive:      "Support Executive",
  verification_executive: "Verification Executive",
};

function Card({ icon: Icon, label, value, hint, tone = "slate", testid }) {
  const toneMap = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
    sky:     "text-sky-700 bg-sky-50 border-sky-100",
    amber:   "text-amber-700 bg-amber-50 border-amber-100",
    slate:   "text-slate-700 bg-white border-slate-200",
    red:     "text-red-700 bg-red-50 border-red-100",
  };
  return (
    <div className={`rounded-md border p-4 ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-bold opacity-70">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="mt-1 font-display text-3xl font-bold tabular">{value}</div>
      {hint && <div className="text-[11px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
}

export function StaffDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/staff/dashboard").then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Load failed"))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="staff-dashboard-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2.5"><LayoutDashboard className="w-7 h-7 text-emerald-600" /> Welcome, {data.full_name}</h1>
        <p className="text-slate-600 mt-1 text-sm">{ROLE_LABEL[data.role] || data.role} · Aap ki assigned pipeline ka snapshot.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Card icon={Hourglass} label="Pending"      value={data.pending}      hint="Contact aur guide karein"    tone="amber"   testid="staff-card-pending" />
        <Card icon={ThumbsUp}  label="Recommended"  value={data.recommended}  hint="Admin approval pending"      tone="sky"     testid="staff-card-recommended" />
        <Card icon={CheckCircle2} label="Verified"  value={data.verified}     hint="Approved ho gaye"            tone="emerald" testid="staff-card-verified" />
        <Card icon={XCircle}   label="Rejected"     value={data.rejected}     hint="Not approved"                tone="red"     testid="staff-card-rejected" />
        <Card icon={Timer}     label="Trial Accounts"  value={data.trial_accounts} hint="Free trial mein"       tone="amber"   testid="staff-card-trial" />
        <Card icon={CheckCircle2} label="Paid Shops"   value={data.paid_shops}    hint="Paid plan par"           tone="emerald" testid="staff-card-paid" />
        <Card icon={TrendingUp}   label="Conversion Rate" value={`${data.conversion_rate}%`} hint="Trial → Paid" tone="sky" testid="staff-card-conv" />
        <Card icon={Timer}     label="Today's Follow-ups" value={data.todays_followups} hint="Pending > 24h old" tone="amber" testid="staff-card-followup" />
      </div>
    </div>
  );
}

function RecommendDialog({ open, onOpenChange, v, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setNote(""); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/verifications/${v.code}/recommend`, { note });
      toast.success("Recommended for approval");
      onOpenChange(false); onDone && onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!v) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Recommend Approval — {v.code}</DialogTitle>
          <DialogDescription>
            Shop: <b>{v.business_name}</b> · Owner: {v.owner_name}
            <div className="mt-1 text-xs text-slate-500">Aap ki recommendation admin ke queue mein highlighted ho jayegi.</div>
          </DialogDescription>
        </DialogHeader>
        <Textarea placeholder="Note for admin (KYC done, business verified, etc.)" value={note} onChange={(e) => setNote(e.target.value)} rows={4} data-testid="staff-recommend-note" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="staff-recommend-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Recommend for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StaffVerifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // "" | pending | recommended | verified | rejected
  const [recV, setRecV] = useState(null);
  const [me, setMe] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/staff/verifications${q}`);
      setRows(data.items || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/staff/me").then((r) => setMe(r.data)).catch(() => {}); }, []);

  const canApprove = !!me?.permissions?.shops?.approve;

  const approveNow = async (code) => {
    if (!window.confirm("Grant final approval and activate the shop?")) return;
    try {
      await api.post(`/admin/verifications/${code}/approve`);
      toast.success("Verified & activated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const filters = [
    { k: "", label: "All" },
    { k: "pending", label: "Pending" },
    { k: "recommended", label: "Recommended" },
    { k: "verified", label: "Verified" },
    { k: "rejected", label: "Rejected" },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="staff-verifications-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2.5"><CheckSquareIcon /> Assigned Verifications</h1>
        <p className="text-slate-600 mt-1 text-sm">Aap ki assigned shop verifications. Contact karein, guide karein, recommend karein.</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {filters.map((f) => (
          <button key={f.k}
            onClick={() => setFilter(f.k)}
            className={`text-xs font-semibold rounded-md px-3 py-1.5 border ${filter === f.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`staff-filter-${f.k || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500 tracking-widest">
            <tr>
              <th className="px-4 py-2">Code</th>
              <th>Shop</th>
              <th>Owner</th>
              <th>Mobile</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-500">Koi verification assigned nahi hai.</td></tr>}
            {rows.map((v) => (
              <tr key={v.code} data-testid={`staff-row-${v.code}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold">{v.code}</td>
                <td>{v.business_name}</td>
                <td className="text-slate-600 text-xs">{v.owner_name}</td>
                <td className="text-xs">
                  {v.mobile && (
                    <a href={`https://wa.me/${v.mobile.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900" data-testid={`staff-wa-${v.code}`}>
                      <MessageCircle className="w-3 h-3" /> {v.mobile}
                    </a>
                  )}
                </td>
                <td>
                  <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${
                    v.status === "verified" ? "bg-emerald-100 text-emerald-700"
                    : v.status === "rejected" ? "bg-red-100 text-red-700"
                    : v.status === "recommended" ? "bg-sky-100 text-sky-700"
                    : "bg-amber-100 text-amber-700"
                  }`}>
                    {v.status}
                  </span>
                </td>
                <td className="py-2 space-x-1">
                  {v.status === "pending" && (
                    <button onClick={() => setRecV(v)} className="text-xs font-bold rounded bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1" data-testid={`staff-recommend-btn-${v.code}`}>
                      <ThumbsUp className="w-3 h-3 inline mr-1" /> Recommend
                    </button>
                  )}
                  {v.status === "recommended" && canApprove && (
                    <button onClick={() => approveNow(v.code)} className="text-xs font-bold rounded bg-sky-600 hover:bg-sky-700 text-white px-2.5 py-1" data-testid={`staff-approve-btn-${v.code}`}>
                      <CheckCircle2 className="w-3 h-3 inline mr-1" /> Approve
                    </button>
                  )}
                  {v.status === "verified" && v.welcome_wa_link && (
                    <a href={v.welcome_wa_link} target="_blank" rel="noreferrer" className="text-xs font-bold rounded bg-emerald-700 hover:bg-emerald-800 text-white px-2.5 py-1 inline-flex items-center gap-1" data-testid={`staff-send-creds-${v.code}`}>
                      <MessageCircle className="w-3 h-3" /> Send Credentials <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RecommendDialog open={!!recV} onOpenChange={(o) => !o && setRecV(null)} v={recV} onDone={load} />
    </div>
  );
}

// tiny local icon so we don't reimport lucide's CheckSquare here
function CheckSquareIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600">
      <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
